import Appearance from "../shapes/Appearance";
import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import { DEFAULT_FILL_COLOR, DEFAULT_STROKE_COLOR } from "../shared/constants";
import EventTypes from "../shared/EventTypes";
import ModeSelector from "../sidebar/edit/Fields/ModeSelector";
import DropdownSelector from "./DropdownSelector";
import { BRING_FORWARD, BRING_TO_FRONT, CHANGE_CANOPY_STYLE, CHANGE_FILL_COLOR, CHANGE_SEGMENT_TYPE, CHANGE_STROKE_COLOR, CHANGE_STROKE_WIDTH, MASS_APPEARANCE, MASS_REORDER, SEND_BACKWARD, SEND_TO_BACK, TOGGLE_FILL, TOGGLE_STROKE } from "../tools/actions/ActionHelper";

export default class DrawMenu extends AbstractProvidedClass {
    appearance = null;
    mode = null;

    constructor(provider) {
        super(provider);

        this.appearance = new Appearance(this.provider, {
            fillColor: this.p.color(DEFAULT_FILL_COLOR),
            strokeColor: this.p.color(DEFAULT_STROKE_COLOR),
            strokeWidth: null,
        });

        this.container = this.p.createDiv()
            .parent('#p5canvas')
            .class('planner-toolbar-vert planner-ui-overlay');

        this.createColorTools();
        this.createStrokeWidth();
        this.createCopyAppearanceButton();
        this.createModeSelector();
        this.createSegmentTypeSelector();
        this.createCanopyStyleSelector();
        this.createOrderButtons();

        this.on(EventTypes.UPDATE_UNIT_SYSTEM, (isMetric) => {
            this.strokeWidthUnit.html(isMetric ? 'm' : 'ft');
            if (this.appearance.strokeWidth) {
                this.strokeWidthInput.value(this.pixelsToDisplay(this.appearance.strokeWidth, isMetric));
            }
        });

        this.hide();
    }

    createColorTools() {
        // Fill color
        let fillLabelRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        this.p.createElement('h4', 'Fill')
            .parent(fillLabelRow)
            .class('input-label');
        this.useFill = this.p.createCheckbox(null, true)
            .parent(fillLabelRow)
            .style('display', 'flex')
            .changed(() => this.changeUseFill());
        this.useFill.elt.firstChild.firstChild.id = 'use-fill';
        this.fillPicker = this.p.createColorPicker(DEFAULT_FILL_COLOR)
            .parent(this.container)
            .class('input-color')
            .mousePressed(() => {
                if (this.mode == 'element') {
                    this.action ??= this.actions.new(CHANGE_FILL_COLOR, this.appearance, this.fillPicker);
                } else if (this.mode == 'selection') {
                    this.action ??= this.actions.new(MASS_APPEARANCE, this.selection.items, { fillColor: this.fillColor });
                }
            })
            .input(() => this.changeFillColor())
            .changed(() => (this.mode == 'element' || this.mode == 'selection') ? this.finishAction() : null);

        // Stroke color
        let strokeLabelRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        this.p.createElement('h4', 'Stroke')
            .parent(strokeLabelRow)
            .class('input-label');
        this.useStroke = this.p.createCheckbox(null, true)
            .parent(strokeLabelRow)
            .style('display', 'flex')
            .changed(() => this.changeUseStroke());
        this.useStroke.elt.firstChild.firstChild.id = 'use-stroke';
        this.strokePicker = this.p.createColorPicker(DEFAULT_STROKE_COLOR)
            .parent(this.container)
            .class('input-color')
            .mousePressed(() => {
                if (this.mode == 'element') {
                    this.action ??= this.actions.new(CHANGE_STROKE_COLOR, this.appearance, this.strokePicker);
                } else if (this.mode == 'selection') {
                    this.action ??= this.actions.new(MASS_APPEARANCE, this.selection.items, { strokeColor: this.strokeColor });
                }
            })
            .input(() => this.changeStrokeColor())
            .changed(() => (this.mode == 'element' || this.mode == 'selection') ? this.finishAction() : null);
    }

    sliderToWidth(raw) {
        const t = raw / 1000;
        return Math.round(Math.pow(1000, t) * 10) / 10;
    }

    widthToSlider(width) {
        return Math.round(Math.log(width) / Math.log(1000) * 1000);
    }

    pixelsToDisplay(px, isMetric = null) {
        return this.calculatePixelToDisplayDistance(px, isMetric);
    }

    displayToPixels(val) {
        const factor = this.settings.metric ? 100 : 30.48;
        return Math.round(val * factor * 10) / 10;
    }

    createStrokeWidth() {
        let widthRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        this.p.createElement('h4', 'Width')
            .parent(widthRow)
            .class('input-label');
        this.useStrokeWidth = this.p.createCheckbox(null, false)
            .parent(widthRow)
            .style('display', 'flex')
            .changed(() => this.toggleStrokeWidth());
        let inputWrapper = this.p.createDiv()
            .parent(this.container)
            .style('position', 'relative')
            .style('width', '100%');
        this.strokeWidthInput = this.p.createInput('0.01', 'text')
            .parent(inputWrapper)
            .attribute('inputmode', 'decimal')
            .style('width', '100%')
            .style('box-sizing', 'border-box')
            .style('padding-right', '2rem')
            .class('input-text')
            .attribute('disabled', true)
            .input(() => {
                this.changeStrokeWidth();
                const pixels = this.displayToPixels(parseFloat(this.strokeWidthInput.value()) || 0.01);
                this.strokeWidthSlider.value(this.widthToSlider(pixels));
            });
        this.strokeWidthInput.elt.onfocus = () => { this.enableHotKeys = false; };
        this.strokeWidthInput.elt.onblur = () => { this.enableHotKeys = true; };
        this.strokeWidthUnit = this.p.createSpan('m')
            .parent(inputWrapper)
            .style('position', 'absolute')
            .style('right', '0.5rem')
            .style('top', '50%')
            .style('transform', 'translateY(-50%)')
            .style('pointer-events', 'none')
            .class('input-label');
        this.strokeWidthSlider = this.p.createSlider(0, 1000, 0, 1)
            .parent(this.container)
            .style('width', '100%')
            .attribute('disabled', true)
            .mousePressed(() => {
                if (this.mode === 'element') {
                    this.action ??= this.actions.new(CHANGE_STROKE_WIDTH, this.appearance, this.appearance.strokeWidth ?? 1);
                } else if (this.mode === 'selection') {
                    this.action ??= this.actions.new(MASS_APPEARANCE, this.selection.items, { strokeWidth: null });
                }
            })
            .touchStarted(() => {
                if (this.mode === 'element') {
                    this.action ??= this.actions.new(CHANGE_STROKE_WIDTH, this.appearance, this.appearance.strokeWidth ?? 1);
                } else if (this.mode === 'selection') {
                    this.action ??= this.actions.new(MASS_APPEARANCE, this.selection.items, { strokeWidth: null });
                }
            })
            .input(() => {
                const pixels = this.sliderToWidth(this.strokeWidthSlider.value());
                this.strokeWidthInput.value(this.pixelsToDisplay(pixels));
                if (this.mode === 'selection') {
                    this.action?.update({ strokeWidth: pixels });
                } else if (this.mode === 'element') {
                    this.action?.update(pixels);
                } else {
                    this.appearance.strokeWidth = pixels;
                }
            })
            .mouseReleased(() => this.finishAction())
            .touchEnded(() => this.finishAction());
    }

    toggleStrokeWidth() {
        const active = this.useStrokeWidth.checked();
        if (active) {
            this.strokeWidthInput.removeAttribute('disabled');
            this.strokeWidthSlider.removeAttribute('disabled');
        } else {
            this.strokeWidthInput.attribute('disabled', true);
            this.strokeWidthSlider.attribute('disabled', true);
        }
        const value = active ? (this.displayToPixels(parseFloat(this.strokeWidthInput.value()) || 0.01)) : null;
        if (this.mode === 'element') {
            this.actions.execute(CHANGE_STROKE_WIDTH, this.appearance, value);
        } else if (this.mode === 'selection') {
            this.actions.execute(MASS_APPEARANCE, this.selection.items, { strokeWidth: value });
        } else {
            this.appearance.strokeWidth = value;
        }
    }

    changeStrokeWidth() {
        const value = this.displayToPixels(parseFloat(this.strokeWidthInput.value()) || 0.01);
        if (this.mode === 'element') {
            this.actions.execute(CHANGE_STROKE_WIDTH, this.appearance, value);
        } else if (this.mode === 'selection') {
            this.actions.execute(MASS_APPEARANCE, this.selection.items, { strokeWidth: value });
        } else {
            this.appearance.strokeWidth = value;
        }
    }

    finishAction() {
        this.action?.finish();
        this.action = null;
    }

    createModeSelector() {
        this.modeSelector = ModeSelector.setup(this.provider)
            .parent(this.container)
            .make()
            .setDrawMode('rectangle');
    }

    buildSegmentIcon(el, value) {
        let svg = SVG().addTo(el).viewbox(0, 0, 30, 30).addClass('icon');
        let stroke = { color: 'currentColor', width: 1.8, linecap: 'round' };
        if (value === 'straight') {
            svg.line(4, 26, 26, 4).stroke(stroke).fill('none');
        } else if (value === 'curve') {
            // Quadratic curve: start bottom-left, control top-center, end top-right
            svg.path('M 4,26 Q 15,2 26,4').stroke(stroke).fill('none');
        } else {
            // Cubic bezier: S-curve shape
            svg.path('M 4,26 C 4,8 15,8 15,15 C 15,22 26,22 26,4').stroke(stroke).fill('none');
        }
    }

    buildCanopyIcon(el, value) {
        let svg = SVG().addTo(el).viewbox(0, 0, 30, 30).addClass('icon');
        if (value === 'smooth') {
            svg.circle(18).cx(15).cy(15).fill('none').stroke({ color: 'currentColor', width: 1.5 });
        } else if (value === 'lobed') {
            let d = 'M';
            for (let j = 0; j <= 40; j++) {
                let a = (j / 40) * Math.PI * 2;
                let r = 9 + Math.sin(a * 10) * 1.2;
                d += `${15 + r * Math.cos(a)},${15 + r * Math.sin(a)} `;
            }
            d += 'Z';
            svg.path(d).fill('none').stroke({ color: 'currentColor', width: 1.5 });
        } else {
            let g = svg.group().stroke({ color: 'currentColor', width: 1.5, linecap: 'round' });
            let numLines = 16;
            for (let j = 0; j < numLines; j++) {
                let a = (j / numLines) * Math.PI * 2;
                let innerR = 1.5;
                let outerR = 5 + (Math.sin(j * 2.7) * 0.5 + 0.5) * 5;
                g.line(
                    15 + innerR * Math.cos(a), 15 + innerR * Math.sin(a),
                    15 + outerR * Math.cos(a), 15 + outerR * Math.sin(a)
                );
            }
        }
    }

    createSegmentTypeSelector() {
        this._segmentType = new DropdownSelector(this.provider, this.container, 'Segment', [
            { value: 'straight', label: 'Straight (SHIFT+T)' },
            { value: 'curve', label: 'Curve (SHIFT+C)' },
            { value: 'bezier', label: 'Bezier (SHIFT+B)' },
        ], (el, val) => this.buildSegmentIcon(el, val));
    }

    get segmentType() {
        return this._segmentType?.value || 'straight';
    }

    set segmentType(val) {
        this._segmentType?.setValue(val);
    }

    showSegmentTypeSelector() {
        this._segmentType.show();
    }

    hideSegmentTypeSelector() {
        this._segmentType.hide();
    }

    createCanopyStyleSelector() {
        this._canopyStyle = new DropdownSelector(this.provider, this.container, 'Canopy', [
            { value: 'smooth', label: 'Smooth (SHIFT+M)' },
            { value: 'lobed', label: 'Lobed (SHIFT+O)' },
            { value: 'conifer', label: 'Conifer (SHIFT+C)' },
        ], (el, val) => this.buildCanopyIcon(el, val));
    }

    get canopyStyle() {
        return this._canopyStyle?.value || 'smooth';
    }

    set canopyStyle(val) {
        this._canopyStyle?.setValue(val);
    }

    showCanopyStyleSelector() {
        this._canopyStyle.show();
    }

    hideCanopyStyleSelector() {
        this._canopyStyle.hide();
    }

    showCanopyStyleForElement(element) {
        this._canopyStyle.setValue(element.canopyStyle ?? 'smooth');
        this._canopyStyle.setOnChange((value) => {
            this.actions.execute(CHANGE_CANOPY_STYLE, element, value);
        });
        this._canopyStyle.show();
    }

    showSegmentTypeForSegment(element, segment) {
        this._segmentType.setValue(segment.type);
        this._segmentType.setOnChange((value) => {
            this.actions.execute(CHANGE_SEGMENT_TYPE, element, segment.index, value);
        });
        this._segmentType.show();
    }

    createCopyAppearanceButton() {
        this.copyAppearanceRow = this.p.createDiv()
            .parent(this.container)
            .class('row');
        this.copyAppearanceBtn = this.p.createDiv()
            .parent(this.copyAppearanceRow)
            .class('btn-icon-clear')
            .style('display', 'flex')
            .style('align-items', 'center')
            .style('justify-content', 'center')
            .mousePressed(() => this.tools.setTool('copy_style', this.appearance));
        if (!this.touch) {
            this.copyAppearanceBtn.attribute('data-tooltip', 'Copy style');
        }
        let svg = SVG().addTo(this.copyAppearanceBtn.elt);
        svg.use('file-copy-line', '/icons/__remixicon.sprite.svg?v=491');
        this.copyAppearanceBtn.elt.children[0].classList.add('icon');
        this.copyAppearanceRow.hide();
    }

    createOrderButtons() {
        this.orderButtons = this.p.createDiv()
            .parent(this.container);
        let orderLabel = this.p.createElement('h4', 'Position')
            .class('input-label')
            .parent(this.orderButtons);

        this.buttonContainer = this.p.createDiv()
            .parent(this.orderButtons)
            .class('row row-between');

        this.createOrderButton(
            'Bring to front',
            () => this.mode === 'selection'
                ? this.actions.execute(MASS_REORDER, this.selection.items, 'front')
                : this.actions.execute(BRING_TO_FRONT, this.selection.first),
            'bring-to-front'
        );
        this.createOrderButton(
            'Send to back',
            () => this.mode === 'selection'
                ? this.actions.execute(MASS_REORDER, this.selection.items, 'back')
                : this.actions.execute(SEND_TO_BACK, this.selection.first),
            'send-to-back'
        );
        this.createOrderButton(
            'Bring forward',
            () => this.mode === 'selection'
                ? this.actions.execute(MASS_REORDER, this.selection.items, 'forward')
                : this.actions.execute(BRING_FORWARD, this.selection.first),
            'bring-forward'
        );
        this.createOrderButton(
            'Send backward',
            () => this.mode === 'selection'
                ? this.actions.execute(MASS_REORDER, this.selection.items, 'backward')
                : this.actions.execute(SEND_BACKWARD, this.selection.first),
            'send-backward'
        );
    }

    createOrderButton(tooltip, func, svgId) {
        let btn = this.p.createDiv()
            .parent(this.buttonContainer)
            .mouseClicked(func)
            .class('btn-icon-clear');
        if (!this.touch) {
            btn.attribute('data-tooltip', tooltip);
        }
        let svg = SVG().addTo(btn.elt);
        svg.use(`${svgId}`, '/icons/__remixicon.sprite.svg?v=491');
        btn.elt.children[0].classList.add('icon');

        return btn;
    }

    disableFill() {
        this.fillWasChecked = this.useFill.checked()
        this.fill = false;
        this.useFill.elt.firstChild.firstChild.disabled = true;
        this.fillPicker.attribute('disabled', true);
    }

    enableFill() {
        if (this.fillWasChecked !== undefined) {
            this.fill = this.fillWasChecked;
            this.fillWasChecked = undefined;
        }
        this.useFill.elt.firstChild.firstChild.disabled = false;
        if (this.useFill.checked()) {
            this.fillPicker.removeAttribute('disabled');
        }
    }

    changeUseFill() {
        this.useFill.checked()
            ? this.fillPicker.removeAttribute('disabled')
            : this.fillPicker.attribute('disabled', true);
        if (this.mode == 'element') {
            this.actions.execute(
                TOGGLE_FILL,
                this.appearance, this.fillPicker.color(), this.useFill.checked()
            );
        } else if (this.mode == 'selection') {
            let value = this.useFill.checked() ? this.fillColor : null;
            this.actions.execute(MASS_APPEARANCE, this.selection.items, { fillColor: value });
        } else {
            this.useFill.checked()
                ? this.appearance.fillColor = this.fillColor
                : this.appearance.fillColor = null;
        }
    }

    changeUseStroke() {
        const strokeOn = this.useStroke.checked();
        strokeOn
            ? this.strokePicker.removeAttribute('disabled')
            : this.strokePicker.attribute('disabled', true);

        if (strokeOn) {
            const wasChecked = this.strokeWidthWasChecked ?? false;
            this.useStrokeWidth.checked(wasChecked);
            if (wasChecked) {
                this.strokeWidthInput.removeAttribute('disabled');
                this.strokeWidthSlider.removeAttribute('disabled');
            }
        } else {
            this.strokeWidthWasChecked = this.useStrokeWidth.checked();
            this.useStrokeWidth.checked(false);
            this.strokeWidthInput.attribute('disabled', true);
            this.strokeWidthSlider.attribute('disabled', true);
        }

        if (this.mode == 'element') {
            this.actions.execute(
                TOGGLE_STROKE,
                this.appearance, this.strokePicker.color(), strokeOn
            );
        } else if (this.mode == 'selection') {
            let changes = { strokeColor: strokeOn ? this.strokeColor : null };
            if (!strokeOn) changes.strokeWidth = null;
            this.actions.execute(MASS_APPEARANCE, this.selection.items, changes);
        } else {
            strokeOn
                ? this.appearance.strokeColor = this.strokeColor
                : this.appearance.strokeColor = null;
        }
    }

    changeFillColor() {
        if (this.mode === 'selection') {
            this.action?.update({ fillColor: this.fillColor });
        } else {
            this.appearance.fillColor = this.fillColor;
        }
    }

    changeStrokeColor() {
        if (this.mode === 'selection') {
            this.action?.update({ strokeColor: this.strokeColor });
        } else {
            this.appearance.strokeColor = this.strokeColor;
        }
    }

    setAppearance(appearance) {
        this.appearance = appearance;
        if (this.appearance.fillColor) {
            this.useFill.checked(true);
            this.fillPicker.value(this.appearance.fillColor.toString('#rrggbb'));
            this.fillPicker.removeAttribute('disabled');
        } else {
            this.useFill.checked(false);
            this.fillPicker.attribute('disabled', true);
        }

        if (this.appearance.strokeColor) {
            this.useStroke.checked(true);
            this.strokePicker.value(this.appearance.strokeColor.toString('#rrggbb'));
            this.strokePicker.removeAttribute('disabled');
        } else {
            this.useStroke.checked(false);
            this.strokePicker.attribute('disabled', true);
        }

        const width = this.appearance.strokeWidth ?? 1;
        this.strokeWidthInput.value(this.pixelsToDisplay(width));
        this.strokeWidthSlider.value(this.widthToSlider(width));
        if (this.appearance.strokeWidth) {
            this.useStrokeWidth.checked(true);
            this.strokeWidthInput.removeAttribute('disabled');
            this.strokeWidthSlider.removeAttribute('disabled');
        } else {
            this.useStrokeWidth.checked(false);
            this.strokeWidthInput.attribute('disabled', true);
            this.strokeWidthSlider.attribute('disabled', true);
        }
    }

    show(mode) {
        this.container.show();
        this.mode = mode;
        if (mode == 'draw') {
            this.modeSelector.show();
            this.orderButtons.hide();
            this.copyAppearanceRow.hide();
        } else if (mode == 'selection') {
            this.modeSelector.hide();
            this.hideSegmentTypeSelector();
            this.hideCanopyStyleSelector();
            this.orderButtons.show();
            this.copyAppearanceRow.hide();
        } else {
            this.modeSelector.hide();
            this.hideSegmentTypeSelector();
            this.hideCanopyStyleSelector();
            this.orderButtons.show();
            this.copyAppearanceRow.show();
        }
    }

    setSelectionAppearance(items) {
        let appearances = items.map(el => el.appearance);

        // Fill
        let fillStrings = appearances.map(a => {
            if (!a.fillColor) return null;
            a.fillColor.setAlpha(255);
            return a.fillColor.toString();
        });
        let allFillSame = fillStrings.every(c => c === fillStrings[0]);
        if (allFillSame && fillStrings[0]) {
            this.useFill.checked(true);
            this.fillPicker.value(appearances[0].fillColor.toString('#rrggbb'));
            this.fillPicker.removeAttribute('disabled');
        } else {
            this.useFill.checked(false);
            this.fillPicker.attribute('disabled', true);
        }

        // Stroke
        let strokeStrings = appearances.map(a => {
            if (!a.strokeColor) return null;
            a.strokeColor.setAlpha(255);
            return a.strokeColor.toString();
        });
        let allStrokeSame = strokeStrings.every(c => c === strokeStrings[0]);
        if (allStrokeSame && strokeStrings[0]) {
            this.useStroke.checked(true);
            this.strokePicker.value(appearances[0].strokeColor.toString('#rrggbb'));
            this.strokePicker.removeAttribute('disabled');
        } else {
            this.useStroke.checked(false);
            this.strokePicker.attribute('disabled', true);
        }

        // Stroke width
        let widths = appearances.map(a => a.strokeWidth);
        let allWidthSame = widths.every(w => w === widths[0]);
        if (allWidthSame && widths[0]) {
            this.useStrokeWidth.checked(true);
            this.strokeWidthInput.value(this.pixelsToDisplay(widths[0]));
            this.strokeWidthSlider.value(this.widthToSlider(widths[0]));
            this.strokeWidthInput.removeAttribute('disabled');
            this.strokeWidthSlider.removeAttribute('disabled');
        } else {
            this.useStrokeWidth.checked(false);
            this.strokeWidthInput.value('');
            this.strokeWidthSlider.value(0);
            this.strokeWidthInput.attribute('disabled', true);
            this.strokeWidthSlider.attribute('disabled', true);
        }
    }

    hide() {
        this.appearance = this.appearance.clone();
        this.container.hide();
    }

    get fillColor() {
        return this.p.color(this.fillPicker.color());
    }

    get strokeColor() {
        return this.p.color(this.strokePicker.color());
    }

    get stroke() {
        return this.useStroke.checked() == 1;
    }

    set stroke(stroke) {
        this.useStroke.checked(stroke ? 1 : 0);
        this.changeUseStroke();
    }

    get fill() {
        return this.useFill.checked() == 1;
    }

    set fill(fill) {
        this.useFill.checked(fill ? 1 : 0);
        this.changeUseFill();
    }
}
